import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { getCreditos } from 'api/pae/creditos';
import { getOrigenesDeCredito } from 'api/pae/origenesDeCredito';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import { onError } from 'utils/handlers';
import { hasPadronPermission } from 'utils/permission/models';
import currencyFormatter from 'utils/toCurrency';

const BusquedaAvanzadaDeCredito = ({
  visible,
  setVisible,
  callback,
  origenDeCredito,
}) => {
  const { padrones: _padrones } = useSelector((s) => s.catalogs);
  const padrones = _padrones.filter((p) => hasPadronPermission(p.id) && p.id !== 5
    && p?.usado_en_ejecuciones);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [origenesDeCredito, setOrigenesDeCredito] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      const _origenesDeCredito = await getOrigenesDeCredito();
      if (mounted) {
        setOrigenesDeCredito(_origenesDeCredito
          .filter((o) => o.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO));
        setLoading(false);
        if (origenDeCredito) {
          form.setFieldsValue({
            origen_del_credito: origenDeCredito,
          });
        } else {
          form.setFieldsValue({
            origen_del_credito: null,
          });
        }
      }
    };
    if (visible) {
      fetchData();
    }
    return () => { mounted = false; };
  }, [visible, origenDeCredito]);

  const onCancel = () => {
    setVisible(false);
    setData([]);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      setSelectedRowKeys([]);
      const params = form.getFieldsValue();
      const keys = Object.keys(params);
      const validProps = keys.filter((prop) => params[prop]);
      params.origen_del_credito = origenDeCredito ?? params?.origen_del_credito;
      let response = null;
      if (validProps.length) {
        response = await getCreditos({ ...params });
        const creditos = response
          .filter((c) => c.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO);
        if (creditos?.length) {
          setData(creditos);
        } else {
          message.warn('No se encontró ningún crédito.');
          setData([]);
        }
      } else {
        message.warn('Debe ingresar al menos un campo.');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onFinishSearch = async (record) => {
    callback(record);
    onCancel();
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const add = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const match = data.find((e) => e.id === key);
      onFinishSearch(match);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancel(),
    },
    {
      text: 'Limpiar',
      icon: <DeleteOutlined />,
      onClick: () => {
        setData([]);
        form.resetFields();
      },
    },
  ];

  const columns = [
    {
      titleText: 'Folio ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      align: 'center',
      render: (text) => (
        <Typography.Text strong>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_de_registro_del_credito',
      key: 'fecha_de_registro_del_credito',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Origen de Crédito',
      dataIndex: 'origen_del_credito',
      key: 'origen_del_credito',
      width: 180,
      render: (text) => (
        <Typography.Text>
          {origenesDeCredito.find((e) => e.id === text)?.descripcion}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 120,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Folio Documento Determinante',
      dataIndex: 'folio_de_documento_determinante',
      key: 'folio_de_documento_determinante',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de la Determinante',
      dataIndex: 'fecha_de_la_determinante',
      key: 'fecha_de_la_determinante',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de Recepción',
      dataIndex: 'fecha_recepcion_de_la_determinante',
      key: 'fecha_recepcion_de_la_determinante',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fecha de Obligación Inicial',
      dataIndex: 'fecha_de_obligacion_inicial_de_adeudos',
      key: 'fecha_de_obligacion_inicial_de_adeudos',
      width: 250,
      align: 'center',
      render: (text) => (
        <Typography.Text>
          {text ? new Date(text).toLocaleDateString('es-MX') : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Total Determinante',
      dataIndex: 'total_determinante',
      key: 'total_determinante',
      width: 250,
      align: 'right',
      render: (text) => (
        <Typography.Text strong>
          {text ? currencyFormatter(text) : '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Permite multiples ejecuciones',
      dataIndex: 'permitir_relacion_a_multiples_ejecuciones',
      key: 'permitir_relacion_a_multiples_ejecuciones',
      width: 250,
      render: (text) => (
        <Typography.Text strong>
          {text ? 'Si' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Tipo de Padrón',
      dataIndex: 'tipo_de_padron',
      key: 'tipo_de_padron',
      width: 180,
      render: (text) => (
        <Typography.Text>
          {padrones.find((e) => e.id === text)?.descripcion}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Clave del Padrón',
      dataIndex: 'clave_del_padron',
      key: 'clave_del_padron',
      width: 180,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Clave Secundaria',
      dataIndex: 'clave_secundaria_del_padron',
      key: 'clave_secundaria_del_padron',
      width: 200,
      align: 'center',
      render: (text) => (
        <Typography.Text code>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Nombre/Razón Social',
      dataIndex: 'nombres_completos_del_padron',
      key: 'nombres_completos_del_padron',
      width: 250,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text}
        </Typography.Text>
      ),
    },
  ];

  return (
    <>
      <Modal
        width="90%"
        open={visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitControls
            label="Búsqueda Avanzada"
            controls={controls}
            loading={loading}
          />
        )}
        onCancel={onCancel}
        maskClosable={!loading}
        keyboard={!loading}
        forceRender
        centered
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSearch}
            initialValues={{ origen_del_credito: origenDeCredito }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="clave"
                  label="Clave"
                  hasFeedback
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="origen_del_credito"
                  label="Origen de Crédito"
                >
                  <Select
                    dataSource={origenesDeCredito}
                    disabled={!!origenDeCredito}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 5]} justify="end">
              <Col xs={24} sm={24} md={6} lg={4}>
                <Button
                  block
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  Buscar
                </Button>
              </Col>
              <Col xs={24} sm={24} md={6} lg={4}>
                <Button
                  block
                  disabled={!selectedRowKeys.length}
                  icon={<PlusOutlined />}
                  onClick={add}
                >
                  Cargar
                </Button>
              </Col>
            </Row>
          </Form>
          <br />
          <Col span={24}>
            <Table
              cols={columns}
              data={data}
              noControls
              allowSort={false}
              allowSearch={false}
              rowSelection={rowSelection}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedRowKeys([record.id]);
                },
                onDoubleClick: add,
              })}
              rowKey="id"
            />
          </Col>
        </Spin>
      </Modal>
    </>
  );
};

BusquedaAvanzadaDeCredito.propTypes = {
  callback: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  origenDeCredito: PropTypes.number,
};

BusquedaAvanzadaDeCredito.defaultProps = { origenDeCredito: null };

export default BusquedaAvanzadaDeCredito;
